import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Home, Clock, CreditCard, ChevronRight, X, DollarSign, Calendar, Zap, Droplets, Wrench, Users, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Contribution {
  id: number;
  type: 'electricity' | 'water' | 'repairs';
  amount: number;
  date: string;
  status: 'pending' | 'completed';
}
interface UtilityBill {
  type: 'electricity' | 'water' | 'repairs';
  totalAmount: number;
  contributors: number;
  yourShare: number;
  paid: number;
  remaining: number;
  dueDate: string;
  icon: any;
  color: string;
}
export function StudentDashboard() {
  const navigate = useNavigate();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [selectedUtility, setSelectedUtility] = useState<UtilityBill | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [contributionAmount, setContributionAmount] = useState('');
  const [maintenanceIssue, setMaintenanceIssue] = useState('');
  const [contributions, setContributions] = useState<Contribution[]>([{
    id: 1,
    type: 'electricity',
    amount: 5000,
    date: '2024-01-15',
    status: 'completed'
  }, {
    id: 2,
    type: 'water',
    amount: 2000,
    date: '2024-01-15',
    status: 'completed'
  }, {
    id: 3,
    type: 'electricity',
    amount: 5000,
    date: '2024-02-15',
    status: 'pending'
  }]);
  const utilityBills: UtilityBill[] = [{
    type: 'electricity',
    totalAmount: 45000,
    contributors: 9,
    yourShare: 5000,
    paid: 5000,
    remaining: 0,
    dueDate: 'Feb 28, 2024',
    icon: Zap,
    color: 'from-yellow-500 to-orange-600'
  }, {
    type: 'water',
    totalAmount: 18000,
    contributors: 9,
    yourShare: 2000,
    paid: 2000,
    remaining: 0,
    dueDate: 'Feb 28, 2024',
    icon: Droplets,
    color: 'from-blue-500 to-cyan-600'
  }, {
    type: 'repairs',
    totalAmount: 27000,
    contributors: 9,
    yourShare: 3000,
    paid: 0,
    remaining: 3000,
    dueDate: 'Feb 25, 2024',
    icon: Wrench,
    color: 'from-purple-500 to-pink-600'
  }];
  const handleBrowseHostels = () => {
    navigate('/find-hostel');
  };
  const handlePaymentHistory = () => {
    alert('Payment History: View all your past transactions and receipts');
  };
  const handleMaintenance = () => {
    setShowMaintenanceModal(true);
  };
  const handleSupport = () => {
    alert('Support: Our team is here to help! Contact us at support@campusaparte.com or call +234 800 CAMPUS');
  };
  const submitPayment = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Payment of ₦${paymentAmount} initiated! You will receive a confirmation shortly.`);
    setShowPaymentModal(false);
    setPaymentAmount('');
  };
  const submitMaintenance = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Maintenance request submitted: "${maintenanceIssue}". Our team will respond within 24 hours.`);
    setShowMaintenanceModal(false);
    setMaintenanceIssue('');
  };
  const handleUtilityClick = (utility: UtilityBill) => {
    setSelectedUtility(utility);
    setContributionAmount(utility.remaining.toString());
    setShowUtilityModal(true);
  };
  const submitContribution = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUtility) return;
    const newContribution: Contribution = {
      id: contributions.length + 1,
      type: selectedUtility.type,
      amount: parseInt(contributionAmount),
      date: new Date().toISOString().split('T')[0],
      status: 'pending'
    };
    setContributions([...contributions, newContribution]);
    alert(`Contribution of ₦${contributionAmount} for ${selectedUtility.type} submitted successfully! Your payment is being processed.`);
    setShowUtilityModal(false);
    setContributionAmount('');
    setSelectedUtility(null);
  };
  const getUtilityIcon = (type: string) => {
    switch (type) {
      case 'electricity':
        return Zap;
      case 'water':
        return Droplets;
      case 'repairs':
        return Wrench;
      default:
        return CreditCard;
    }
  };
  const totalPaidThisMonth = contributions.filter(c => c.date.startsWith('2024-02') && c.status === 'completed').reduce((sum, c) => sum + c.amount, 0);
  const totalPendingThisMonth = contributions.filter(c => c.date.startsWith('2024-02') && c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
  return <DashboardLayout userType="student" data-id="element-1129" data-mp="wz0y77cbveo9">
      {/* Payment Modal */}
      <AnimatePresence data-id="element-1130" data-mp="wyra5paufhl7">
        {showPaymentModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowPaymentModal(false)} data-id="element-1131" data-mp="1bo6wmt3cd0v3">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1132" data-mp="sxyzcrmbp4mt">
              <div className="flex justify-between items-center mb-6" data-id="element-1133" data-mp="26fwmxw9n1rv0">
                <h3 className="text-xl font-bold text-brand-dark" data-id="element-1134" data-mp="2bezt5k1cu44k">
                  Make Payment
                </h3>
                <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1135" data-mp="2gg71bk7et5br">
                  <X className="w-5 h-5" data-id="element-1136" data-mp="1nqqcsxmwu8kt" />
                </button>
              </div>

              <form onSubmit={submitPayment} className="space-y-4" data-id="element-1137" data-mp="1spb427k6phx4">
                <div className="bg-slate-50 rounded-lg p-4 mb-4" data-id="element-1138" data-mp="1spkwe1spc15z">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-1139" data-mp="1xncu5cg12hi3">
                    Current Balance Due
                  </div>
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-1140" data-mp="22gfgp0me0qos">
                    ₦0.00
                  </div>
                </div>

                <Input label="Payment Amount (₦)" type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="Enter amount" required data-id="element-1141" data-mp="19ya4pif5jkd1" />

                <div data-id="element-1142" data-mp="3e7vkipyrbley">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1143" data-mp="3e7c2zad0qz1g">
                    Payment Method
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1144" data-mp="39boc8qiw1xtu">
                    <option data-id="element-1145" data-mp="2zhxsfyxpibkh">Card Payment</option>
                    <option data-id="element-1146" data-mp="2vhnm2l605j53">Bank Transfer</option>
                    <option data-id="element-1147" data-mp="2vif56wiexoeh">USSD</option>
                  </select>
                </div>

                <Button type="submit" className="w-full" data-id="element-1148" data-mp="2qk3vcls4bdw9">
                  <DollarSign className="w-4 h-4 mr-2" data-id="element-1149" data-mp="2ljg6a5ng974o" />
                  Proceed to Payment
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Maintenance Modal */}
      <AnimatePresence data-id="element-1150" data-mp="99xvjnzuoytc">
        {showMaintenanceModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowMaintenanceModal(false)} data-id="element-1151" data-mp="dbjglwmjgjem">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1152" data-mp="3qi339qx4e1rf">
              <div className="flex justify-between items-center mb-6" data-id="element-1153" data-mp="37pqr6cgnzm43">
                <h3 className="text-xl font-bold text-brand-dark" data-id="element-1154" data-mp="2o4qky8rezejp">
                  Maintenance Request
                </h3>
                <button onClick={() => setShowMaintenanceModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1155" data-mp="2k4zxadx6peje">
                  <X className="w-5 h-5" data-id="element-1156" data-mp="2t3tp7qzklgqm" />
                </button>
              </div>

              <form onSubmit={submitMaintenance} className="space-y-4" data-id="element-1157" data-mp="373w5x9w5644a">
                <div data-id="element-1158" data-mp="3721kzvkvec07">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1159" data-mp="332so4huj9y0f">
                    Issue Type
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1160" data-mp="3gsmex5plmscs">
                    <option data-id="element-1161" data-mp="3qrlpittxcy2f">Plumbing</option>
                    <option data-id="element-1162" data-mp="3qsd9t8i2n9de">Electrical</option>
                    <option data-id="element-1163" data-mp="3ltk6g8wi0yqy">AC/Heating</option>
                    <option data-id="element-1164" data-mp="3lulf66q1l95i">Security</option>
                    <option data-id="element-1165" data-mp="1l85e5bdz5ybw">Other</option>
                  </select>
                </div>

                <div data-id="element-1166" data-mp="1ghp1hthzagql">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1167" data-mp="1gh6xubszl08n">
                    Description
                  </label>
                  <textarea value={maintenanceIssue} onChange={e => setMaintenanceIssue(e.target.value)} placeholder="Describe the issue in detail..." className="w-full h-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none" required data-id="element-1168" data-mp="1bivl9tbb4bmq" />
                </div>

                <Button type="submit" className="w-full" data-id="element-1169" data-mp="1q8b9p2nd84z9">
                  Submit Request
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Utility Contribution Modal */}
      <AnimatePresence data-id="element-1170" data-mp="eufrfl15zg3t">
        {showUtilityModal && selectedUtility && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowUtilityModal(false)} data-id="element-1171" data-mp="2qh9mffw20ft3">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1172" data-mp="395ekc6q156jz">
              <div className="flex justify-between items-center mb-6" data-id="element-1173" data-mp="1osfnnyflwqgd">
                <h3 className="text-xl font-bold text-brand-dark capitalize" data-id="element-1174" data-mp="1ssgao7gru9z1">
                  {selectedUtility.type} Contribution
                </h3>
                <button onClick={() => setShowUtilityModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1175" data-mp="2r40qixhndwgl">
                  <X className="w-5 h-5" data-id="element-1176" data-mp="351fjudb0uodu" />
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-1177" data-mp="310n7hb6us3dc">
                <div className="flex items-center justify-between mb-4" data-id="element-1178" data-mp="3f0y46uuugc6f">
                  <div data-id="element-1179" data-mp="3a2wgtxqdxqp5">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-1180" data-mp="3a1lf03gi2t0o">
                      Total Bill
                    </div>
                    <div className="text-2xl font-bold text-brand-dark" data-id="element-1181" data-mp="3oqzrvljabskt">
                      ₦{selectedUtility.totalAmount.toLocaleString()}
                    </div>
                  </div>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedUtility.color} flex items-center justify-center`} data-id="element-1182" data-mp="1nl3z0l1pvq1">
                    <selectedUtility.icon className="w-8 h-8 text-white" data-id="element-1183" data-mp="g98ssz271of8" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1184" data-mp="l7a7l7sl71kw">
                  <div data-id="element-1185" data-mp="79ddqj5ip99x">
                    <div className="text-slate-500" data-id="element-1186" data-mp="78280dsihrg6">Your Share</div>
                    <div className="font-bold text-brand-dark" data-id="element-1187" data-mp="b9w6qgj5cldp">
                      ₦{selectedUtility.yourShare.toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-1188" data-mp="29qxs1yb1exf">
                    <div className="text-slate-500" data-id="element-1189" data-mp="2a90nhfh2reb">Contributors</div>
                    <div className="font-bold text-brand-dark" data-id="element-1190" data-mp="2bk4fh8x3z5f">
                      {selectedUtility.contributors} people
                    </div>
                  </div>
                  <div data-id="element-1191" data-mp="3oplh41sgo7wl">
                    <div className="text-slate-500" data-id="element-1192" data-mp="3omzcob0va8il">Paid</div>
                    <div className="font-bold text-emerald-600" data-id="element-1193" data-mp="3aq2lg8gu6p16">
                      ₦{selectedUtility.paid.toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-1194" data-mp="37mwf4xpb07wp">
                    <div className="text-slate-500" data-id="element-1195" data-mp="37ng0koe5xefh">Remaining</div>
                    <div className="font-bold text-red-600" data-id="element-1196" data-mp="32ol6urenq5gl">
                      ₦{selectedUtility.remaining.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between" data-id="element-1197" data-mp="2txcga2yufuso">
                  <span className="text-sm text-slate-600" data-id="element-1198" data-mp="2oyhowcmt8xwy">Due Date</span>
                  <span className="text-sm font-bold text-brand-dark" data-id="element-1199" data-mp="2jymxm7z4i6bd">
                    {selectedUtility.dueDate}
                  </span>
                </div>
              </div>

              <form onSubmit={submitContribution} className="space-y-4" data-id="element-1200" data-mp="2axos1l91dqqo">
                <Input label="Contribution Amount (₦)" type="number" value={contributionAmount} onChange={e => setContributionAmount(e.target.value)} placeholder="Enter amount" required data-id="element-1201" data-mp="2azhyp0jngayo" />

                <div className="flex gap-2" data-id="element-1202" data-mp="1r24auuyt0nai">
                  <Button type="button" variant="outline" size="sm" onClick={() => setContributionAmount((selectedUtility.yourShare / 2).toString())} data-id="element-1203" data-mp="1w28kc11aohr8">
                    Half (₦{(selectedUtility.yourShare / 2).toLocaleString()})
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setContributionAmount(selectedUtility.yourShare.toString())} data-id="element-1204" data-mp="1d6a1yby15xxe">
                    Full (₦{selectedUtility.yourShare.toLocaleString()})
                  </Button>
                </div>

                <div data-id="element-1205" data-mp="xgqb48hntwt9">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1206" data-mp="xfyc0noutgy8">
                    Payment Method
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1207" data-mp="17fp6wqhbtxyu">
                    <option data-id="element-1208" data-mp="12eru6tf35w4q">Card Payment</option>
                    <option data-id="element-1209" data-mp="eqi77o64z2c0">Bank Transfer</option>
                    <option data-id="element-1210" data-mp="ep73focoxukw">USSD</option>
                  </select>
                </div>

                <Button type="submit" className="w-full" data-id="element-1211" data-mp="9u05kx63rk3w">
                  <DollarSign className="w-4 h-4 mr-2" data-id="element-1212" data-mp="ohd0vdlpp47h" />
                  Pay ₦{parseInt(contributionAmount || '0').toLocaleString()}
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1213" data-mp="3s3ttjkj6ucwu">
        <Card className="bg-gradient-to-br from-brand-primary to-purple-600 text-white border-none" data-id="element-1214" data-mp="3n6k2y1k2bv7f">
          <CardContent className="p-6" data-id="element-1215" data-mp="22nq3ewokfott">
            <div className="flex justify-between items-start mb-4" data-id="element-1216" data-mp="27fhkmglu0i7a">
              <div className="bg-white/20 p-2 rounded-lg" data-id="element-1217" data-mp="27huarh1qtyvf">
                <Home className="w-6 h-6 text-white" data-id="element-1218" data-mp="1tpe508hgjmqt" />
              </div>
              <Badge className="bg-white/20 text-white border-none" data-id="element-1219" data-mp="1xpoi053o3df6">
                Active
              </Badge>
            </div>
            <div className="text-3xl font-bold mb-1" data-id="element-1220" data-mp="2lgu06qx2yhsn">Emerald Heights</div>
            <div className="text-purple-100 text-sm" data-id="element-1221" data-mp="2qf582w3tei88">
              Room 302 • Expires in 45 days
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1222" data-mp="2cgyjk6kr9ul8">
          <CardContent className="p-6" data-id="element-1223" data-mp="2ch6uhsxbipts">
            <div className="flex justify-between items-start mb-4" data-id="element-1224" data-mp="2hd5p3y98p42n">
              <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600" data-id="element-1225" data-mp="10zp4rxcknqq9">
                <CreditCard className="w-6 h-6" data-id="element-1226" data-mp="111ryarcxo1zq" />
              </div>
            </div>
            <div className="text-3xl font-bold text-brand-dark mb-1" data-id="element-1227" data-mp="1603d7gi352ot">
              ₦{totalPaidThisMonth.toLocaleString()}
            </div>
            <div className="text-slate-500 text-sm" data-id="element-1228" data-mp="1snia0p4z16na">
              Utilities paid this month
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1229" data-mp="1nop5vnbkqrzs">
          <CardContent className="p-6" data-id="element-1230" data-mp="1nq9smqrnd6n3">
            <div className="flex justify-between items-start mb-4" data-id="element-1231" data-mp="1iplznzjuyfq8">
              <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600" data-id="element-1232" data-mp="1irfbdt9bvq53">
                <Users className="w-6 h-6" data-id="element-1233" data-mp="2gc1hj2g75feb" />
              </div>
            </div>
            <div className="text-3xl font-bold text-brand-dark mb-1" data-id="element-1234" data-mp="2bf2tn1n4m4ok">9</div>
            <div className="text-slate-500 text-sm" data-id="element-1235" data-mp="26m9le3sg8e7q">Roommates contributing</div>
          </CardContent>
        </Card>
      </div>

      {/* Utility Bills Section */}
      <Card className="mb-8" data-id="element-1236" data-mp="2z3c6vf69xk43">
        <CardHeader data-id="element-1237" data-mp="2z3lxn4z57vau">
          <CardTitle data-id="element-1238" data-mp="2z4440ufirpwh">Monthly Utility Contributions</CardTitle>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1239" data-mp="2uc3xuk8uw0rg">
            Split bills fairly with your roommates
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1240" data-mp="31pbjf7p2a4va">
          {utilityBills.map((utility, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.1
        }} className="relative overflow-hidden rounded-xl border border-slate-200 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleUtilityClick(utility)} data-id="element-1241" data-mp="36oxy7lkjft67" data-mpi={idx}>
              <div className={`absolute inset-0 bg-gradient-to-br ${utility.color} opacity-5`} data-id="element-1242" data-mp="364xwagrt0tz"></div>

              <div className="relative p-6" data-id="element-1243" data-mp="860tblbejjxx">
                <div className="flex items-center justify-between mb-4" data-id="element-1244" data-mp="834ttsjwon7d">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${utility.color} flex items-center justify-center`} data-id="element-1245" data-mp="c7m8jdbesaba">
                    <utility.icon className="w-6 h-6 text-white" data-id="element-1246" data-mp="1glhxmvnrlqj5" />
                  </div>
                  {utility.remaining === 0 ? <CheckCircle className="w-6 h-6 text-emerald-600" data-id="element-1247" data-mp="1ljjfxedd9ltk" /> : <AlertCircle className="w-6 h-6 text-orange-500" data-id="element-1248" data-mp="1po97ir0zse8o" />}
                </div>

                <h3 className="text-lg font-bold text-brand-dark capitalize mb-1" data-id="element-1249" data-mp="1uliy4a04avy3">
                  {utility.type}
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-1250" data-mp="24budqtt5ytkt">
                  Due: {utility.dueDate}
                </p>

                <div className="space-y-2 mb-4" data-id="element-1251" data-mp="29aymxkg38rb5">
                  <div className="flex justify-between text-sm" data-id="element-1252" data-mp="29ci0h3ad0o98">
                    <span className="text-slate-600" data-id="element-1253" data-mp="2ddld7gx42468">Your Share:</span>
                    <span className="font-bold text-brand-dark" data-id="element-1254" data-mp="2iatwjktzwe92">
                      ₦{utility.yourShare.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1255" data-mp="1ox4arpdacmd">
                    <span className="text-slate-600" data-id="element-1256" data-mp="1nly6l8g8ss3">Paid:</span>
                    <span className="font-bold text-emerald-600" data-id="element-1257" data-mp="3try1jb0bd5ko">
                      ₦{utility.paid.toLocaleString()}
                    </span>
                  </div>
                  {utility.remaining > 0 && <div className="flex justify-between text-sm" data-id="element-1258" data-mp="pd803gg7hm3s">
                      <span className="text-slate-600" data-id="element-1259" data-mp="lcebslyuq5ad">Remaining:</span>
                      <span className="font-bold text-red-600" data-id="element-1260" data-mp="gm7nkn894msn">
                        ₦{utility.remaining.toLocaleString()}
                      </span>
                    </div>}
                </div>

                {/* Progress Bar */}
                <div className="mb-4" data-id="element-1261" data-mp="193a6bquozelb">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1262" data-mp="14bs7p9g55q3i">
                    <div className={`h-full bg-gradient-to-r ${utility.color} transition-all duration-500`} style={{
                  width: `${utility.paid / utility.yourShare * 100}%`
                }} data-id="element-1263" data-mp="149p9410xaon6"></div>
                  </div>
                </div>

                <Button size="sm" className="w-full" variant={utility.remaining === 0 ? 'outline' : 'default'} disabled={utility.remaining === 0} data-id="element-1264" data-mp="1igshyymt7wpr">
                  {utility.remaining === 0 ? 'Paid in Full' : 'Contribute Now'}
                </Button>
              </div>
            </motion.div>)}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1265" data-mp="zj7sbpc80u43">
        {/* Contribution History */}
        <Card data-id="element-1266" data-mp="2pw6zqfk3i9cr">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-1267" data-mp="2pvnhsyug3kyq">
            <CardTitle data-id="element-1268" data-mp="2tv633vq6hy23">Contribution History</CardTitle>
            <Button variant="ghost" size="sm" data-id="element-1269" data-mp="i93xuv27g1v6">
              View All
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1270" data-mp="e82772mcsy4t">
            {contributions.slice().reverse().map((contribution, idx) => {
            const Icon = getUtilityIcon(contribution.type);
            return <div key={contribution.id} className="p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-center justify-between" data-id="element-1271" data-mp="n6vzwhwkd4d3" data-mpi={idx}>
                    <div className="flex items-center gap-3" data-id="element-1272" data-mp="1axt396aamtcq">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center" data-id="element-1273" data-mp="1ax01yku3sw1z">
                        <Icon className="w-5 h-5 text-slate-600" data-id="element-1274" data-mp="15wlqstx7h5zq" />
                      </div>
                      <div data-id="element-1275" data-mp="3cpeet5hsjabj">
                        <div className="font-medium text-brand-dark capitalize" data-id="element-1276" data-mp="37q30s48t6ixn">
                          {contribution.type}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1277" data-mp="3a46o3w2st8xf">
                          {contribution.date}
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1278" data-mp="3j5xuz492rlsv">
                      <div className="font-bold text-brand-dark" data-id="element-1279" data-mp="3o4957g3a7ybm">
                        -₦{contribution.amount.toLocaleString()}
                      </div>
                      <Badge variant={contribution.status === 'completed' ? 'success' : 'warning'} className="text-xs" data-id="element-1280" data-mp="2qik6lujfzykd">
                        {contribution.status}
                      </Badge>
                    </div>
                  </div>;
          })}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-id="element-1281" data-mp="2cmznoz3prgcy">
          <CardHeader data-id="element-1282" data-mp="255jlcm9qg3wf">
            <CardTitle data-id="element-1283" data-mp="25634u7f170ck">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4" data-id="element-1284" data-mp="206qgfi8ihl7v">
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-primary/5" onClick={handleBrowseHostels} data-id="element-1285" data-mp="2079s4h7kjddg">
              <Home className="w-6 h-6" data-id="element-1286" data-mp="1m6p7dfi3f7hb" />
              <span data-id="element-1287" data-mp="1m7qqm799j0a4">Browse Hostels</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50" onClick={handlePaymentHistory} data-id="element-1288" data-mp="1hfp9a6x8mjd8">
              <CreditCard className="w-6 h-6" data-id="element-1289" data-mp="17jdsp4qm77cz" />
              <span data-id="element-1290" data-mp="12iyhojtt2by6">Payment History</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50" onClick={handleMaintenance} data-id="element-1291" data-mp="12igbaudfihcj">
              <Clock className="w-6 h-6" data-id="element-1292" data-mp="svchrmwrlcsd" />
              <span data-id="element-1293" data-mp="swf91vy01h8t">Maintenance</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-slate-500 hover:text-brand-dark hover:bg-slate-50" onClick={handleSupport} data-id="element-1294" data-mp="evkxj4fnn05x">
              <ChevronRight className="w-6 h-6" data-id="element-1295" data-mp="17k09fi3hp2d1" />
              <span data-id="element-1296" data-mp="17mcyef7o0czl">Support</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>;
}